import { useId } from "react";
import { cn } from "@/lib/cn";

interface FieldProps {
  label: string;
  /** Explains the field before it is filled in, not after it fails. */
  hint?: string;
  error?: string;
  required?: boolean;
  children: (props: {
    id: string;
    "aria-describedby": string | undefined;
    /** Set while an error shows, so `.field` paints the control red. */
    "aria-invalid": true | undefined;
  }) => React.ReactNode;
  className?: string;
}

/**
 * Label, control, hint and error as one unit.
 *
 * The point is the wiring, not the layout: the label's `htmlFor`, and an
 * `aria-describedby` that points at whichever of hint/error is showing. Done by
 * hand at each call site, one of those two is always eventually missing, and
 * the failure is invisible to everyone not using a screen reader.
 *
 * `children` is a render prop so the control keeps its own type — an `<input>`,
 * a `<select>` and a checkbox group all need the generated id, and cloning a
 * child element to inject it loses the types.
 */
export function Field({ label, hint, error, required, children, className }: FieldProps) {
  const id = useId();
  const hintId = `${id}-hint`;
  const errorId = `${id}-error`;

  // The error replaces the hint rather than stacking with it: two lines of
  // small grey-and-red text under one input is harder to read than one.
  const describedBy = error ? errorId : hint ? hintId : undefined;

  return (
    <div className={cn("block", className)}>
      <label
        htmlFor={id}
        className="text-content mb-1.5 flex items-center gap-1 text-xs font-medium"
      >
        {label}
        {required ? (
          <span className="text-danger" aria-hidden="true">
            *
          </span>
        ) : null}
      </label>

      {children({ id, "aria-describedby": describedBy, "aria-invalid": error ? true : undefined })}

      {error ? (
        <p id={errorId} role="alert" className="text-danger mt-1.5 text-xs">
          {error}
        </p>
      ) : hint ? (
        <p id={hintId} className="text-content-subtle mt-1.5 text-xs leading-relaxed">
          {hint}
        </p>
      ) : null}
    </div>
  );
}

/**
 * Checkbox with a label beside it.
 *
 * `accent-color` rather than a custom-drawn box: the native control already
 * has the right focus ring, the right hit area on touch, and the indeterminate
 * state, and re-implementing it with a div reliably loses one of the three.
 */
export function Checkbox({
  label,
  description,
  checked,
  onChange,
  disabled,
  className,
}: {
  label: React.ReactNode;
  description?: string;
  checked: boolean;
  onChange: (checked: boolean) => void;
  disabled?: boolean;
  className?: string;
}) {
  return (
    <label
      className={cn(
        "group flex cursor-pointer items-start gap-2.5 rounded-control px-2 py-1.5 transition-colors",
        disabled ? "cursor-not-allowed opacity-55" : "hover:bg-surface-overlay",
        className,
      )}
    >
      <input
        type="checkbox"
        checked={checked}
        disabled={disabled}
        onChange={(event) => onChange(event.target.checked)}
        className="accent-brand mt-0.5 size-3.5 shrink-0"
      />
      <span className="min-w-0">
        <span className="text-content block text-[0.8125rem] leading-tight">{label}</span>
        {description ? (
          <span className="text-content-subtle mt-0.5 block text-xs">{description}</span>
        ) : null}
      </span>
    </label>
  );
}
